export type AdminPermissionKey = string;

/** Used by routes + sidebar. When permissions are null (logged out), returns true. */
export const hasAdminPermission = (
  permissions: Record<string, boolean> | null | undefined,
  permission?: AdminPermissionKey,
): boolean => {
  // Before login / permissions load, keep routes so PrivateRoute can redirect
  if (!permissions || !permission) {
    return true;
  }

  if (permissions[permission] !== true) {
    return false;
  }

  // *_manage also requires corresponding *_view when that key exists
  if (permission.endsWith("_manage")) {
    const viewPermission = permission.replace(/_manage$/, "_view");
    if (
      Object.prototype.hasOwnProperty.call(permissions, viewPermission) &&
      permissions[viewPermission] !== true
    ) {
      return false;
    }
  }

  return true;
};

/** Report submenu items and their view permissions (sidebar order). */
export const ADMIN_REPORT_ITEMS: {
  path: string;
  permission: AdminPermissionKey;
  label: string;
}[] = [
  {
    path: "/admin/reports/package",
    permission: "reportspackagesubscription_view",
    label: "Package Subscriptions",
  },
  {
    path: "/admin/reports/residents",
    permission: "reportsresident_view",
    label: "Residents",
  },
  {
    path: "/admin/reports/members",
    permission: "reportsbuildingmember_view",
    label: "Members",
  },
  {
    path: "/admin/reports/futurebookings",
    permission: "reportsbooking_view",
    label: "Future Bookings",
  },
  {
    path: "/admin/reports/coupons",
    permission: "reportsvoucher_view",
    label: "Coupons",
  },
  {
    path: "/admin/reports/profit",
    permission: "reportsownerprofit_view",
    label: "Profit",
  },
  {
    path: "/admin/reports/profit-quarter",
    permission: "reportsquarterprofit_view",
    label: "Profit Quarter",
  },
  {
    path: "/admin/reports/pastbookings",
    permission: "reportsbooking_view",
    label: "Past Bookings",
  },

  {
    path: "/admin/reports/amenityusage",
    permission: "reportsamenityusage_view",
    label: "Amenity Usage",
  },
  {
    path: "/admin/reports/doorhistory",
    permission: "buildings_doorlog",
    label: "Door History",
  },
];

export const hasAnyReportPermission = (
  permissions: Record<string, boolean> | null | undefined,
): boolean =>
  ADMIN_REPORT_ITEMS.some((item) =>
    hasAdminPermission(permissions, item.permission),
  );

/** Sidebar-order landing pages used as post-login / home fallback. */
const ADMIN_HOME_CANDIDATES: {
  path: string;
  permission: AdminPermissionKey;
}[] = [
  { path: "/admin", permission: "dashboard_view" },
  { path: "/admin/buildings", permission: "buildings_view" },
  { path: "/admin/groups", permission: "groups_view" },
  { path: "/admin/members", permission: "members_view" },
  { path: "/admin/roles", permission: "roles_view" },
  { path: "/admin/users", permission: "users_view" },
  { path: "/admin/coupons", permission: "vouchers_view" },
  ...ADMIN_REPORT_ITEMS.map(({ path, permission }) => ({ path, permission })),
];

export const getFirstPermittedAdminPath = (
  permissions: Record<string, boolean> | null | undefined,
): string => {
  if (!permissions) {
    return "/admin";
  }

  const match = ADMIN_HOME_CANDIDATES.find((candidate) =>
    hasAdminPermission(permissions, candidate.permission),
  );

  return match?.path ?? "/admin";
};
